import { AppWindow } from '../../definitions/AppWindow';

declare let window: AppWindow;

const DATA_POPOVER_ID = 'data-popover-id';
const DATA_POPOVER_AUTODISPLAY = 'data-popover-autodisplay';

const openOrCloseCwPopover = ({ popoverEl, openCwPopover, closeCwPopover }) => {
  if (!popoverEl.classList.contains('cw_popoverIsOpening')) {
    openCwPopover();
  } else {
    closeCwPopover();
  }
};

const shouldCloseCwPopover = ({ popoverEl, target, closeCwPopover }) => {
  if (!popoverEl.contains(target)) {
    closeCwPopover();
  }
};

function getOpeningTriggerEl(doc) {
  return doc.getElementById('header-notifications');
}

function getPopoverEl(doc) {
  return doc.getElementById('notifications-popover');
}

function getNotifications(doc) {
  return doc.querySelectorAll('li.cw_notification');
}

export default function initPopover(win, doc) {
  'use strict';
  /**
   * Grab the main parent element for event delegation, rather than
   * attaching an event listener to each overflow-menu trigger instance
   */

  const root = doc.body;
  const notificationsImpressionStatus = {};

  function trackNotificationImpression(alertId, impressionUrl) {
    if (!notificationsImpressionStatus[alertId] && impressionUrl) {
      impressionUrl += '&ts=' + Date.now();
      const xhttp = new XMLHttpRequest();
      xhttp.open('GET', impressionUrl, true);
      xhttp.withCredentials = true;
      xhttp.send();

      // mark alert impression tracking event fired as true
      notificationsImpressionStatus[alertId] = true;
    }
  }

  function checkAndTrackImpression() {
    const notifications = getNotifications(doc) || [];
    for (const notification of notifications) {
      const impressionUrl = notification.getAttribute('data-fpti-impression-url');
      trackNotificationImpression(notification.id, impressionUrl);
    }
  }

  function openCwPopover() {
    const openingTriggerEl = getOpeningTriggerEl(doc);
    const popoverEl = getPopoverEl(doc);
    if (!openingTriggerEl || !popoverEl) {
      return;
    }

    const trackingPagenameHide = openingTriggerEl.getAttribute('data-popover-pagename-hide') || '';
    /**
     * @function finishTransition - responsible for cleaning up after transitions
     */
    function finishTransition() {
      /**
       * Set the pagename to close so next time the click registers, FPTI will track the close event
       */
      openingTriggerEl.setAttribute('data-pagename', trackingPagenameHide);
      /**
       * Add and remove the appropriate classes
       */
      root.classList.add('cw_hasOpenPopover');
      root.setAttribute(DATA_POPOVER_ID, 'notifications-popover');
      popoverEl.classList.remove('cw_popOverIsOpening');
      popoverEl.classList.add('cw_popoverIsOpen');
      const popoverIsOpen = createNewEvent('popoverIsOpen');
      popoverEl.dispatchEvent(popoverIsOpen);
      /**
       * Remove event listener.
       */
      popoverEl.removeEventListener('transitionend', finishTransition);
      /**
       * Focus on popover container
       */
      popoverEl.focus();
      /**
       * Dispatch open event
       */
      const open = createNewEvent('open');
      popoverEl.dispatchEvent(open);

      checkAndTrackImpression();
    }
    /**
     * Prep the popover for animation. I.e. add change from `display: none`
     */
    popoverEl.classList.add('cw_popoverPrepToOpen');
    const popoverBodyEl = popoverEl.querySelector('.cw_popover-body');
    popoverEl.style.left = -(popoverEl.clientWidth / 2 - openingTriggerEl.clientWidth / 2) + 'px';
    /**
     * Prevents the page/body from scrolling when scrolling the notifications window
     */
    popoverBodyEl?.addEventListener('wheel', function (event) {
      const deltaY = event.deltaY;
      const contentHeight = popoverBodyEl.scrollHeight;
      const visibleHeight = popoverBodyEl.offsetHeight;
      const scrollTop = popoverBodyEl.scrollTop;
      if ((scrollTop === 0 && deltaY < 0) || (visibleHeight + scrollTop === contentHeight && deltaY > 0)) {
        event.preventDefault();
      }
    });
    /**
     * Wait for DOM to update and register new `display` prop for popover
     */
    setTimeout(function () {
      /**
       * Dispatch prep to open event
       */
      const prepToOpen = createNewEvent('prepToOpen');
      popoverEl.dispatchEvent(prepToOpen);
      popoverEl.classList.add('cw_popoverIsOpening');
      /**
       * Add minimal a11y support in case no aria describedby is used
       */
      if (!popoverEl.hasAttribute('aria-label')) {
        popoverEl.setAttribute('aria-label', 'Popover Dialog');
      }
    }, 35);
    /**
     * Does client have transitionEnd event?
     * If not, fallback to setTimeout
     */
    // eslint-disable-next-line no-undef
    if (doc.body.style.transition === '') {
      popoverEl.addEventListener('transitionend', finishTransition);
    } else {
      setTimeout(finishTransition, 220);
    }
  }
  function closeCwPopover() {
    const openingTriggerEl = getOpeningTriggerEl(doc);
    const popoverEl = getPopoverEl(doc);
    if (!openingTriggerEl || !popoverEl) {
      return;
    }
    /**
     * Grab original scroll position before opening the popover.
     * Grab popover reference or just grab the current open popover element.
     */
    const trackingPagenameShow = openingTriggerEl.getAttribute('data-popover-pagename-show') || '';
    /**
     * @function finishTransition - responsible for cleaning up after transitions
     */
    function finishTransition() {
      if (!openingTriggerEl || !popoverEl) {
        return;
      }
      /**
       * Set tracking pagename
       */
      openingTriggerEl.setAttribute('data-pagename', trackingPagenameShow);
      /**
       * Add and remove the appropriate classes
       */
      popoverEl.classList.remove('cw_popoverIsClosing');
      popoverEl.classList.remove('cw_popoverPrepToOpen');
      popoverEl.classList.remove('cw_popoverIsOpening');
      popoverEl.classList.remove('cw_popoverIsOpen');

      /**
       * Remove event listener.
       */
      popoverEl.removeEventListener('transitionend', finishTransition);
      /**
       * Dispatch closed event
       */
      const closed = createNewEvent('closed');
      popoverEl.dispatchEvent(closed);
    }
    /**
     * Prep body and popover for close animation
     */
    root.classList.remove('cw_hasOpenPopover');
    root.removeAttribute(DATA_POPOVER_ID);
    popoverEl.classList.remove('cw_popoverIsOpen');
    popoverEl.classList.add('cw_popoverIsClosing');
    /**
     * Does client have transitionEnd event?
     */
    // eslint-disable-next-line no-undef
    if (doc.body.style.transition === '') {
      popoverEl.addEventListener('transitionend', finishTransition);
    } else {
      setTimeout(finishTransition, 220);
    }

    // remove auto open attr if there
    const autoOpen = openingTriggerEl.getAttribute(DATA_POPOVER_AUTODISPLAY);
    if (autoOpen === 'true') {
      openingTriggerEl.removeAttribute(DATA_POPOVER_AUTODISPLAY);
    }
  }

  /**
   * @function -  polyfill function for creating custom events in IE 9-11
   * @param {string} eventName
   */
  function createNewEvent(eventName) {
    let event = null;
    if (typeof Event === 'function') {
      event = new Event(eventName);
    } else {
      event = doc.createEvent('Event');
      event.initEvent(eventName, true, true);
    }
    return event;
  }

  /**
   * @function - adds `click` event listener to body
   *
   * Note: Uses "event delegation" to conserve memory and handle dynamic elements
   */
  root.addEventListener('click', function (event) {
    const target = event.target;
    const className = target.className;
    const popoverIsOpen = root.classList.contains('cw_hasOpenPopover');
    /**
     * Are we clicking on a trigger to open or close popover?
     * Will close the popover when clicking outside of it
     *
     * Note: check for existence of `indexOf` to prevent errors when clicking SVGs
     */
    if (className && className.indexOf) {
      const popoverEl = getPopoverEl(doc);
      if (!popoverEl) {
        return;
      }
      if (className.indexOf('cw_popover-trigger') !== -1) {
        event.preventDefault();
        openOrCloseCwPopover({
          popoverEl,
          openCwPopover,
          closeCwPopover,
        });
      } else {
        if (popoverIsOpen) {
          shouldCloseCwPopover({
            popoverEl,
            target,
            closeCwPopover,
          });
        }
      }
    }
  });
  /**
   * @function - adds `keyup` event listener to container element
   *
   * Note: listens for ESC key only
   */
  root.addEventListener('keyup', function (event) {
    const popoverTrigger = doc.querySelector('.cw_popover-trigger');
    if (event.keyCode === 27 && doc.body.className.indexOf('cw_hasOpenPopover') !== -1) {
      closeCwPopover();
      popoverTrigger.focus();
    }
  });
  /**
   * @function -  initializes auto display funcitonality for mobile & desktop
   *
   */
  function autoDisplayInit() {
    const mobilePopoverTrigger = doc.querySelector('.cw_notifications-mobile-trigger');
    const mobileNav = doc.querySelector('.cw_navbar_mobile');
    const openingTriggerEl = doc.getElementById('header-notifications');
    const autoOpen = openingTriggerEl?.getAttribute(DATA_POPOVER_AUTODISPLAY) || '';
    /**
     * Desktop
     */
    if (autoOpen === 'true') {
      openCwPopover();
    }
    /**
     * Mobile
     */
    if (mobilePopoverTrigger && mobileNav) {
      const mobileAutoOpen = mobilePopoverTrigger.getAttribute(DATA_POPOVER_AUTODISPLAY);
      let modalId = null;
      let modal = null;
      const mobileNavDisplay = mobileNav.currentStyle
        ? mobileNav.currentStyle.display
        : getComputedStyle(mobileNav, null).display;
      if (mobileAutoOpen === 'true' && mobileNavDisplay !== 'none') {
        modalId = mobilePopoverTrigger.getAttribute('data-modal-id');
        modal = doc.getElementById(modalId);
        modal.classList.add('cw_notifications_critical');
        doc.body.appendChild(modal);
        setTimeout(function () {
          win.window.CWHelpers.modal.open(modalId);
        }, 1000);
      }
    }
  }
  /**
   * Run auto display after the dom has loaded
   */
  doc.addEventListener('DOMContentLoaded', autoDisplayInit);
}
